import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InfoNote } from "@/components/shared";
import { Building2, CheckCircle2, Database, Gauge, Globe, KeyRound, Loader2, Play, Search } from "lucide-react";
import { toast } from "sonner";

const COUNTRIES = ["US", "GB", "AU", "CA", "NZ", "IE", "DE", "FR", "ES", "IT", "NL", "IN", "SG", "ZA", "BR", "JP"];
const BUSINESS_TYPES = [
  { v: "services", l: "Professional services" }, { v: "local", l: "Local business / tradie" },
  { v: "ecommerce", l: "Ecommerce" }, { v: "saas", l: "SaaS / software" },
  { v: "content", l: "Content / media" }, { v: "other", l: "Other" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { activeWorkspace, activeWebsite, loadWorkspaces } = useApp();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [wsName, setWsName] = useState("");
  const [site, setSite] = useState({ name: "", domain: "", defaultCountry: "US", defaultLanguage: "en", timezone: "UTC", businessType: "services" });
  const [createdSite, setCreatedSite] = useState(null);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [aiKey, setAiKey] = useState("");
  const [aiConnected, setAiConnected] = useState(false);
  const [job, setJob] = useState(null);
  const [demoLoaded, setDemoLoaded] = useState(false);

  useEffect(() => {
    const s = params.get("step");
    if (s === "website" && activeWorkspace) setStep(1);
    else if (s === "workspace") setStep(0);
    else if (activeWorkspace && !activeWebsite) setStep(1);
  }, []); // eslint-disable-line

  useEffect(() => {
    api.get("/integrations/google/config-status").then((r) => setGoogleStatus(r.data)).catch(() => {});
  }, []);

  const wsId = useMemo(() => activeWorkspace?.id, [activeWorkspace]);
  const targetSite = createdSite || activeWebsite;

  const createWorkspace = async () => {
    setBusy(true);
    try {
      await api.post("/workspaces", { name: wsName });
      await loadWorkspaces();
      toast.success("Workspace created");
      setStep(1);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const createSite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/websites`, site);
      setCreatedSite(data);
      await loadWorkspaces(wsId, data.id);
      toast.success("Website added");
      setStep(2);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const connectGoogle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/websites/${targetSite.id}/google/authorize`);
      window.location.assign(data.authorization_url);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const connectAI = async () => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/ai/connect`, { provider: "openai", api_key: aiKey });
      setAiConnected(true);
      toast.success("OpenAI connected - key encrypted server-side");
      setStep(5);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const runAnalysis = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/websites/${targetSite.id}/audit`);
      setJob(data);
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.id}`);
          setJob(j);
          if (j.status === "completed") {
            clearInterval(poll);
            toast.success("First SEO analysis complete");
            setBusy(false);
          } else if (j.status === "failed") {
            clearInterval(poll);
            toast.error(j.error || "Crawl failed");
            setBusy(false);
          }
        } catch (e) { clearInterval(poll); setBusy(false); }
      }, 2500);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  const loadDemo = async () => {
    try {
      await api.post(`/websites/${targetSite.id}/demo-data`);
      setDemoLoaded(true);
      toast.success("DEMO DATA loaded (clearly labelled everywhere)");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const STEPS = ["Workspace", "Website", "Search Console", "GA4", "AI (optional)", "First analysis"];

  return (
    <div className="min-h-screen bg-background">
      <header className="flex h-14 items-center border-b bg-card px-4 sm:px-6">
        <div className="flex items-center gap-2">
          <Gauge className="h-5 w-5 text-primary" />
          <span className="text-sm font-bold tracking-tight">SIX9 SEO</span>
        </div>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={() => navigate("/dashboard")} data-testid="onboarding-skip-to-dashboard">
          Go to dashboard
        </Button>
      </header>
      <div className="mx-auto max-w-2xl px-4 py-8 sm:py-12">
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {STEPS.map((s, i) => (
              <button key={s} onClick={() => i < step && setStep(i)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${i === step ? "border-primary bg-primary/5 text-primary" : i < step ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "text-muted-foreground"}`}>
                {i < step ? <CheckCircle2 className="mr-1 inline h-3 w-3" /> : null}{s}
              </button>
            ))}
          </div>
          <Progress value={(step / (STEPS.length - 1)) * 100} className="mt-3 h-1.5" />
        </div>

        {step === 0 && (
          <Card className="shadow-none">
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Building2 className="h-5 w-5 text-primary" /> Create your workspace</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">Workspaces hold websites, connections and team members. Agencies typically create one per client.</p>
              <div className="space-y-1.5">
                <Label htmlFor="ws">Workspace name</Label>
                <Input id="ws" data-testid="onboarding-workspace-name-input" value={wsName} onChange={(e) => setWsName(e.target.value)} placeholder="e.g. My Agency" />
              </div>
              {activeWorkspace && (
                <InfoNote>You already have workspace “{activeWorkspace.name}”. Create another or <button className="text-primary underline" onClick={() => setStep(1)}>continue with it</button>.</InfoNote>
              )}
              <Button onClick={createWorkspace} disabled={!wsName.trim() || busy} data-testid="onboarding-create-workspace-button">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create workspace
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 1 && (
          <Card className="shadow-none">
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Globe className="h-5 w-5 text-primary" /> Add your website</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Website name</Label>
                  <Input data-testid="onboarding-website-name-input" value={site.name} onChange={(e) => setSite({ ...site, name: e.target.value })} placeholder="e.g. Acme Plumbing" />
                </div>
                <div className="space-y-1.5">
                  <Label>Domain</Label>
                  <Input data-testid="onboarding-website-domain-input" value={site.domain} onChange={(e) => setSite({ ...site, domain: e.target.value })} placeholder="example.com" />
                </div>
                <div className="space-y-1.5">
                  <Label>Country</Label>
                  <Select value={site.defaultCountry} onValueChange={(v) => setSite({ ...site, defaultCountry: v })}>
                    <SelectTrigger data-testid="onboarding-country-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Language</Label>
                  <Input value={site.defaultLanguage} onChange={(e) => setSite({ ...site, defaultLanguage: e.target.value })} placeholder="en" />
                </div>
                <div className="space-y-1.5">
                  <Label>Timezone</Label>
                  <Input value={site.timezone} onChange={(e) => setSite({ ...site, timezone: e.target.value })} placeholder="UTC" />
                </div>
                <div className="space-y-1.5">
                  <Label>Business type</Label>
                  <Select value={site.businessType} onValueChange={(v) => setSite({ ...site, businessType: v })}>
                    <SelectTrigger data-testid="onboarding-business-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{BUSINESS_TYPES.map((b) => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={createSite} disabled={!site.name.trim() || !site.domain.trim() || busy} data-testid="onboarding-create-website-button">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add website
              </Button>
            </CardContent>
          </Card>
        )}

        {(step === 2 || step === 3) && (
          <Card className="shadow-none">
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Search className="h-5 w-5 text-primary" /> Connect {step === 2 ? "Google Search Console" : "Google Analytics 4"}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {step === 2
                  ? "Search Console provides the query, click, impression and position evidence that powers the opportunity engine."
                  : "GA4 adds organic sessions and conversions so prioritisation is commercial, not just traffic."}
                {" "}One Google connection covers both.
              </p>
              {googleStatus?.configured ? (
                <Button onClick={connectGoogle} disabled={busy} data-testid="onboarding-connect-google-button">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Connect Google
                </Button>
              ) : (
                <div className="space-y-3">
                  <InfoNote>
                    Google connections are temporarily unavailable on this deployment. The SIX9 platform team needs to enable them;
                    no Google Cloud setup is required from you. You can continue and explore everything else meanwhile.
                  </InfoNote>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(step + 1)} data-testid="onboarding-skip-google-button">Skip for now</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 4 && (
          <Card className="shadow-none">
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><KeyRound className="h-5 w-5 text-primary" /> Connect AI (optional, BYOK)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Bring your own OpenAI key to enable Approve &amp; Run automation. The platform works fully without it -
                you'll still get audits, opportunities and recommendations. AI usage is billed by your provider.
              </p>
              <div className="space-y-1.5">
                <Label>OpenAI API key</Label>
                <Input type="password" data-testid="onboarding-ai-key-input" value={aiKey} onChange={(e) => setAiKey(e.target.value)} placeholder="sk-..." />
                <p className="text-xs text-muted-foreground">Encrypted server-side. Never shown again - only a masked form like ••••••••1234.</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={connectAI} disabled={!aiKey.trim() || busy} data-testid="onboarding-connect-ai-button">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Validate & connect
                </Button>
                <Button variant="outline" onClick={() => setStep(5)} data-testid="onboarding-skip-ai-button">Skip for now</Button>
              </div>
              <InfoNote>GitHub and deployment providers can be connected any time from the Connections page.</InfoNote>
            </CardContent>
          </Card>
        )}

        {step === 5 && (
          <Card className="shadow-none">
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Play className="h-5 w-5 text-primary" /> Run your first SEO analysis</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                We'll crawl <span className="font-mono text-xs">{targetSite?.domain}</span> (respecting robots.txt and rate limits),
                build the page inventory, run the technical audit and compute your Internal SEO Health Score.
              </p>
              {job ? (
                <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{job.message}</span>
                    <span className="text-xs text-muted-foreground">{job.status}</span>
                  </div>
                  <Progress value={job.progress} className="h-1.5" data-testid="onboarding-audit-progress" />
                  {job.status === "completed" && (
                    <div className="text-xs text-emerald-700" data-testid="onboarding-audit-complete">
                      {job.result?.pages} pages crawled · {job.result?.issues} issues found
                    </div>
                  )}
                  {job.status === "failed" && <div className="text-xs text-rose-700">{job.error}</div>}
                </div>
              ) : (
                <Button onClick={runAnalysis} disabled={busy} data-testid="onboarding-run-analysis-button">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Run first analysis
                </Button>
              )}
              <div className="rounded-md border p-3">
                <div className="flex items-center gap-2 text-sm font-medium"><Database className="h-4 w-4 text-violet-600" /> No Google connection yet?</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Load clearly-labelled DEMO DATA to explore the opportunity engines. It is purged the moment a real Google sync succeeds.
                </p>
                <Button size="sm" variant="outline" className="mt-2" onClick={loadDemo} disabled={demoLoaded} data-testid="onboarding-load-demo-button">
                  {demoLoaded ? "Demo data loaded" : "Load demo data"}
                </Button>
              </div>
              <Button variant={job?.status === "completed" ? "default" : "outline"} onClick={() => navigate("/dashboard")} data-testid="onboarding-finish-button">
                Open dashboard
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
